import { emitter, EventType } from '@/utils/emitter.utils';
import { cloneDeep, round } from 'lodash-es';
import { KonvaDrag } from './konva.drag';
import { KonvaUtils } from './konva.utils';
import { Shape, ShapeType } from '../shape';
import { Stage } from 'konva/lib/Stage';
import Konva from 'konva/lib/Core';
import { Node } from 'konva/lib/Node';
import { Transformer } from 'konva/lib/shapes/Transformer';
import { Layer } from 'konva/lib/Layer';
import { Group } from 'konva/lib/Group';
import { KonvaLayout } from './konva.layout';
import { KonvaTransform } from './konva.transform';
import { KonvaHover } from './konva.hover';
import { KonvaGroup } from './konva.group';
import { KonvaSelection } from './konva.selection';
import { Rect } from 'konva/lib/shapes/Rect';
import { Image } from 'konva/lib/shapes/Image';
import { ResourceKey } from '../shape/attributes';

export default class KonvaService {
  private static _ins: KonvaService | undefined;
  static callbackFns: (() => void)[] = [];

  private _enable = true;
  set enable(v: boolean) {
    this._enable = v;
    if (!v) this.clear();
  }
  get enable() {
    return this._enable;
  }

  stage!: Stage;
  layer: Layer | undefined;
  tr: Transformer | undefined;

  watchfaceCanvas = {
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
    scale: 1
  };

  // 组合实例
  selection: KonvaSelection | undefined;
  hover: KonvaHover | undefined;

  private constructor(
    id: string,
    { width, height }: { width: number; height: number }
  ) {
    if (this.stage) {
      this.stage.destroy();
    }
    const stage = new Konva.Stage({
      container: id,
      width,
      height
    });
    this.stage = stage;

    // 需要先绑定此事件
    new KonvaGroup(this);
    this.selection = new KonvaSelection(this);
    KonvaUtils.bindKeyBoard(stage);

    // 注册点击空白事件
    stage.on('click', (e) => {
      const target = e.target;
      if (target == stage) {
        emitter.emit(EventType['konva:click-blank-area']);
      }
    });
  }

  static init(id: string, size: { width: number; height: number }) {
    this._ins = new this(id, size);
    return this._ins;
  }

  static getIns() {
    if (!this._ins) {
      console.warn('KonvaService未初始化，请在初始化后再获取实例');
      // console.trace();
    }
    return this._ins;
  }

  deleteShape(name: string) {
    const shape = this.stage.findOne('.' + name);
    shape?.destroy();
  }

  setSize({ width, height }: { width: number; height: number }) {
    this.stage.setSize({ width, height });
  }

  /**
   * 获取所有的 shapes
   * @param interactable 是否过滤不可交互的，默认为false
   * @returns
   */
  getShapeNodes(interactable = false) {
    let nodes = this.stage
      // 所有的shape的id=shape
      .find('#shape');
    if (interactable) {
      nodes = nodes.filter((node) => KonvaUtils.isNodeEnabled(node));
    }
    return nodes;
  }

  get isTransforming() {
    return KonvaTransform.TRANSFORMING || KonvaDrag.DRAGGING;
  }

  clear() {
    this.stage.getLayers().forEach((layer) => {
      layer.destroy();
    });
    this.tr = undefined;
    this.layer = undefined;
  }

  private _lastDrawData: {
    shapes: Shape[];
    options?: {
      selected?: string[];
      disableDrag?: boolean;
      disableTransform?: boolean;
    };
  } = { shapes: [] };

  drawShapes(
    shapes: Shape[],
    options?: {
      selected?: string[];
      disableDrag?: boolean;
      disableTransform?: boolean;
    },
    force?: boolean
  ) {
    this._lastDrawData = { shapes, options };
    const { selected, disableDrag, disableTransform } = options ?? {};
    if (this.isTransforming || (!this.enable && force)) return;
    const stage = this.stage;
    console.time('drawShapes');

    // 记录所有explode的group
    const groupsExplode = stage
      .find('#group')
      .filter((group) => group.getAttr('data')?.explode)
      .map((group) => group.name());

    // 清理所有layer
    this.clear();

    // layer
    const layer = new Layer({
      x: this.watchfaceCanvas.offsetX,
      y: this.watchfaceCanvas.offsetY,
      scale: {
        x: this.watchfaceCanvas.scale,
        y: this.watchfaceCanvas.scale
      }
    });

    this.layer = layer;

    const layout = new KonvaLayout();
    // transformer
    const tr = layout.drawTransformer(disableTransform);
    this.tr = tr;

    // 绘制引导线需要先执行
    if (!disableDrag) {
      const drag = new KonvaDrag(this);
      drag.bindEvent();
    }

    // 保存旧的 hover 状态
    const previousCenterPointShapeName = this.hover?.getCurrentCenterPointShapeName();
    this.hover = new KonvaHover(this);
    // 恢复中心点状态
    if (previousCenterPointShapeName) {
      this.hover.setCurrentCenterPointShapeName(previousCenterPointShapeName);
    }

    const transform = new KonvaTransform(this);

    const _addShapes = (
      shapes: Shape[],
      target: Layer | Group,
      parentShape?: Shape
    ) => {
      const rects: Node[] = [];
      shapes.forEach((shape) => {
        const isGroup = !!shape.children?.length;
        const isSlot = shape.type == ShapeType.Slot;
        const rect = layout.drawShape(shape, parentShape);

        if (!rect) return;

        rects.push(rect);
        // add to target
        target.add(rect);
        if (disableTransform) {
          rect.setAttr('draggable', false);
        } else {
          transform.bindEvent(rect);
        }

        if (isGroup) {
          // 需要创建group
          const name = 'group_' + rect.name();
          const attrs = cloneDeep(rect.attrs);
          const rectData = rect.getAttr?.('data') ?? attrs.data ?? {};
          // 进度条组的xy的值
          const logicalPos = rectData.progressBarGroupLogicalPosition as
            | { x: number; y: number }
            | undefined;
          const groupAttrs = {
            ...attrs,
            id: 'group',
            draggable: false,
            name: name,
            data: {
              ...attrs.data,
              rect: rect,
              explode: groupsExplode.includes(name)
            }
          };
          //（进度条可添加进度条背景/指示器图片等）这个if中控制的是进度条本身的xy位置
          if (logicalPos) {
            groupAttrs.x = logicalPos.x;
            groupAttrs.y = logicalPos.y;
          }
          const group = new Group(groupAttrs);

          target.add(group);
          const childern: Shape[] = [...(shape.children ?? [])];

          // 如果是容器里的widget
          if (isSlot) {
            // 需要判断要渲染的widget
            const { widgetIdToDisplay = '' } = shape.attrs;
            if (widgetIdToDisplay == 'none') {
              // 不渲染任何子元素
              childern.length = 0;
            } else {
              // 需要渲染id=widgetIdToDisplay的子元素
              const widgetToDisplay =
                childern?.find((item) => item.id == widgetIdToDisplay) ??
                childern?.[childern.length - 1];

              if (widgetToDisplay) {
                // 仅渲染需要展示的widget
                childern.length = 0;
                childern.push(widgetToDisplay);
              }
            }
          }
          const rects = _addShapes(childern, group, shape);

          rect.setAttr('data', {
            ...rect.getAttr('data'),
            group,
            children: rects
          });
        }
      });

      return rects;
    };

    // draw shapes
    // 所有shapes
    _addShapes(shapes, layer);

    // add to stage
    layer.add(tr);
    stage.add(layer);
    console.timeEnd('drawShapes');

    // 恢复默认选项
    const defaultSelectNodes =
      selected ??
      this.getShapeNodes().filter((node) =>
        this.selection?.selected.includes(node.name())
      );

    if (defaultSelectNodes.length) {
      this.selection?.selectNodes(defaultSelectNodes, {
        fire: false,
        interactable: false
      });
    }

    // 由于无需根据实际运行值更改尺寸，此处代码暂时不需要
    // while (KonvaService.callbackFns.length) {
    //   const fn = KonvaService.callbackFns.shift();
    //   fn?.();
    // }

    // 刷新中心点位置（如果正在显示的话）
    this.hover?.refreshCenterPoint();

    // finally
    // console.log('draw shapes in konva', this.getShapeNodes());
  }

  getClientRect(shapeId: string, isGroup = false) {
    const shape = Shape.getShape(shapeId);
    if (!shape) return;
    const { type } = shape;

    if (!this.tr || !this.layer) {
      this.drawShapes(this._lastDrawData.shapes, {
        ...this._lastDrawData.options
      });
    }

    const rect = this.stage.find('.' + shapeId)?.[0];
    let rect2: Rect | undefined = undefined;
    const parent = (rect?.parent ?? this.layer) as Layer | Group;
    // 成组时不需要重新计算位置
    if (
      (shape.isPointerImage ||
        type == ShapeType.Text ||
        type == ShapeType.ImageArray) &&
      !isGroup
    ) {
      const {
        x = 0,
        y = 0,
        width = 0,
        height = 0,
        offsetX = 0,
        offsetY = 0
      } = rect?.getAttr('attrs') ?? {};
      rect2 = new Rect({
        width: width,
        height: height,
        rotation: 0,
        // 当为图组类的数字填充时，不处理offsetX
        x: x - (type == ShapeType.ImageArray ? 0 : offsetX),
        y: y - offsetY
      });
      parent?.add(rect2 as Rect);
    }
    if (isGroup && type == ShapeType.ImageArray) {
      const { sourceItems } = shape;
      let _maxWidth = 0;
      const {
        x,
        y,
        w,
        h,
        rotation,
        fillType,
        resources,
        decimalOffsetX,
        align
      } = shape.attrs;
      const min = Math.min(...sourceItems.map((item) => item?.minValue ?? 0));
      const _max = Math.max(...sourceItems.map((item) => item?.maxValue ?? 0));
      const includePrecision = sourceItems.some((item) => {
        return !!item?.precision;
      });
      let unitIconCounts = 0;
      if (resources) {
        const resourceNames = Object.keys(resources) as ResourceKey[];
        const unitIconNames = resourceNames.filter((key) =>
          key.includes('unitIcon')
        );
        unitIconNames.forEach((key) => {
          const unitIcon = resources[key];
          if (unitIcon) {
            Object.keys(unitIcon).forEach((colorId) => {
              if (unitIcon[colorId].length !== 0) unitIconCounts++;
            });
          }
        });
      }

      if (!w) return;

      if (fillType == '数字填充' && !shape.widgetImageArray) {
        if (sourceItems) {
          if (_max) {
            let maxLen = _max?.toString().length;
            // 小数和小数点
            if (includePrecision) {
              let _precision = 0;
              sourceItems.forEach((item) => {
                if (item?.precision && item.precision > _precision)
                  _precision = item?.precision;
              });
              maxLen += Number(_precision) + 1;
            }
            // 单位
            if (unitIconCounts) maxLen += unitIconCounts;
            // 负号
            if (min && min < 0) maxLen += 1;
            _maxWidth =
              (maxLen ?? 0) * (w ?? 0) +
              (maxLen - 1) * (shape.attrs.space ?? 0);
            console.log(`[ALIGN-DEBUG] getClientRect(isGroup) | name=${shape.attrs.name} align=${align} fillType=${fillType} maxValue=${_max} maxLen=${maxLen} attrs.w=${w} space=${shape.attrs.space} _maxWidth=${_maxWidth}`);
          }
        }
      } else if (shape.widgetImageArray) {
        // widgetImageArray 使用 Widget flex 布局渲染，优先用 runtime 实际渲染尺寸
        // 这与元素未成组时的边框尺寸一致（KonvaLayout 中 baseAttrs.width = runtime?.width）
        const runtime = KonvaUtils.getRuntimData(shape.id);
        if (runtime?.width && runtime?.height) {
          const _scale = shape.attrs.scale ?? 1;
          const finalScale = isNaN(_scale) ? 1 : _scale;
          const finalX = isNaN(Number(x)) ? 0 : Number(x) || 0;
          const finalY = isNaN(Number(y)) ? 0 : Number(y) || 0;
          rect2 = new Rect({
            width: runtime.width * finalScale,
            height: runtime.height * finalScale,
            rotation: rotation ?? 0,
            x: finalX,
            y: finalY
          });
          parent?.add(rect2 as Rect);
        } else {
          // runtime 未就绪时回退到粗略估算，避免组边框过小
          _maxWidth =
            (4 + (unitIconCounts > 0 ? unitIconCounts : 0)) * (w ?? 0);
        }
      }
      if (_maxWidth && x !== undefined) {
        // 图层缩放值
        const _scale = shape.attrs.scale ?? 1;
        if (Number(decimalOffsetX) !== 0) {
          const decimalOffsetXNum = Number(decimalOffsetX);
          if (!isNaN(decimalOffsetXNum)) {
            _maxWidth += decimalOffsetXNum;
          }
        }

        // 确保所有值都是有效数字
        const finalMaxWidth = isNaN(_maxWidth) ? w ?? 0 : _maxWidth;
        const finalW = isNaN(Number(w)) ? 0 : Number(w) || 0;
        const finalH = isNaN(Number(h)) ? 0 : Number(h) || 0;
        const finalX = isNaN(Number(x)) ? 0 : Number(x) || 0;
        const finalY = isNaN(Number(y)) ? 0 : Number(y) || 0;
        const finalScale = isNaN(_scale) ? 1 : _scale;

        rect2 = new Rect({
          width: (finalMaxWidth ?? finalW) * finalScale,
          height: finalH * finalScale,
          rotation: rotation ?? 0,
          x:
            align == 'center'
              ? finalX - finalMaxWidth / 2
              : align == 'right'
              ? finalX - finalMaxWidth
              : finalX,
          y: finalY
        });
        parent?.add(rect2 as Rect);
      }
    }

    let {
      x = 0,
      y = 0,
      width = 0,
      height = 0
    } = (rect2 ? rect2?.getClientRect() : rect?.getClientRect()) ?? {};
    const scale = this.layer?.scale()?.x ?? 1;
    const offsetX = this.layer?.x() ?? 0;
    const offsetY = this.layer?.y() ?? 0;

    rect2?.destroy();

    // 当shape在stage中不可见时，即shape是slot中不显示的widget或是slot的子widget中不显示的元素
    if (!rect && !rect2) {
      const {
        x: attrsX = 0,
        y: attrsY = 0,
        w: attrsW = 0,
        h: attrsH = 0
      } = shape.attrs ?? {};
      // 不可见的元素为相对位置，使用母元素的x y
      const parentAttrs = shape.parent?.attrs ?? {};
      const { x: parentX = 0, y: parentY = 0 } = parentAttrs;
      const grandParentAttrs = shape.parent?.parent?.attrs ?? {};
      const { x: grandParentX = 0, y: grandParenY = 0 } = grandParentAttrs;
      const resultX = Number(attrsX) + Number(parentX) + Number(grandParentX);
      const resultY = Number(attrsY) + Number(parentY) + Number(grandParenY);
      const resultW = Number(attrsW) / scale;
      const resultH = Number(attrsH) / scale;
      return {
        x: isNaN(resultX) ? 0 : round(resultX),
        y: isNaN(resultY) ? 0 : round(resultY),
        width: isNaN(resultW) ? 0 : round(resultW),
        height: isNaN(resultH) ? 0 : round(resultH)
      };
    }

    // 检查并处理 NaN 值
    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
      const {
        x: attrsX = 0,
        y: attrsY = 0,
        w: attrsW = 0,
        h: attrsH = 0
      } = shape.attrs ?? {};
      if (isNaN(x)) x = Number(attrsX) || 0;
      if (isNaN(y)) y = Number(attrsY) || 0;
      if (isNaN(width)) width = Number(attrsW) || 0;
      if (isNaN(height)) height = Number(attrsH) || 0;
    }

    const resultX = (x - offsetX) / scale;
    const resultY = (y - offsetY) / scale;
    const resultW = width / scale;
    const resultH = height / scale;

    if (isGroup && type == ShapeType.ImageArray) {
      console.log(`[ALIGN-DEBUG] getClientRect RETURN | name=${shape.attrs.name} align=${shape.attrs.align} => {x:${round(resultX)}, w:${round(resultW)}, h:${round(resultH)}}`);
    }

    return {
      x: isNaN(resultX) ? 0 : round(resultX),
      y: isNaN(resultY) ? 0 : round(resultY),
      width: isNaN(resultW) ? 0 : round(resultW),
      height: isNaN(resultH) ? 0 : round(resultH)
    };
  }

  rotatedImage(imgEl: HTMLImageElement, rotation: number) {
    const { width, height } = imgEl;
    const img = new Image({
      x: 9999,
      y: 9999,
      image: imgEl,
      width: width,
      height: height,
      rotation
    });
    this.layer?.add(img);
    const { width: width2, height: height2 } = img.getClientRect();
    const res = {
      dataUrl: img.toDataURL(),
      offsetX: width / 2 - width2 / 2,
      offsetY: height / 2 - height2 / 2
    };
    img.destroy();
    return res;
  }
}
